import {
  extensionOf,
  fileKindOf,
  formatBytes,
  isViewableInBrowser,
  type FileKind,
} from '@obras/shared';
import {
  CircleAlert,
  Download,
  DraftingCompass,
  Eye,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  ScanLine,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';

/*
 * As peças de uma linha de arquivo, usadas onde quer que um arquivo apareça:
 * no dossiê da obra e na lista de documentos de cada relatório. Um só lugar
 * para que a linha, o olho e o download sejam iguais nas duas telas.
 */

const ICON = { strokeWidth: 1.5 } as const;

/**
 * A extensão no lugar de um ícone por formato — no formulário de anexo e nos
 * arquivos escolhidos do novo relatório.
 *
 * Um `.sldprt` não tem ícone que alguém reconheça, e a sigla é exatamente o que
 * quem trabalha com o arquivo procura na lista.
 */
export function Selo({ extension }: { extension: string }) {
  return (
    <span className="tabular grid h-9 w-12 shrink-0 place-items-center bg-sunken font-mono text-[10px] tracking-wide text-ink-3 uppercase">
      {extension || '—'}
    </span>
  );
}

/**
 * Baixa o arquivo pela API, e guarda o que aconteceu.
 *
 * O link não pode apontar para a rota direto: o token de acesso vive em memória
 * e viaja no cabeçalho, que um `<a href>` não manda. Então o arquivo vem por
 * `fetch` e o download dispara a partir do blob — o mesmo caminho dos relatórios
 * em PDF. É também pela API que o arquivo sai com o nome que tinha.
 *
 * `documentId` é o id do anexo do dossiê ou, para um arquivo enviado num
 * relatório, o id da mídia: a rota resolve os dois. A falha fica guardada até a
 * próxima tentativa: antes a promessa rejeitada sumia, e o clique simplesmente
 * não dava em nada.
 */
function useBaixar(obraId: string, documentId: string, originalName: string) {
  const [baixando, setBaixando] = useState(false);
  const [falhou, setFalhou] = useState(false);

  function baixar(): void {
    if (baixando) return;
    setBaixando(true);
    setFalhou(false);
    api
      .download(`/obras/${obraId}/documentos/${documentId}/arquivo`, originalName)
      .catch(() => setFalhou(true))
      .finally(() => setBaixando(false));
  }

  return { baixando, falhou, baixar };
}

/** O ícone de cada família de arquivo, no quadro escuro da linha. */
const ICONE_DA_FAMILIA: Record<FileKind, LucideIcon> = {
  documento: FileText,
  planilha: FileSpreadsheet,
  projeto: DraftingCompass,
  levantamento: ScanLine,
  pacote: FileArchive,
  imagem: FileImage,
};

/** As ações da ponta: quadradas, da altura da linha, escurecendo até a chapa cheia sob o ponteiro. */
const ACAO_DA_PONTA =
  'grid w-11 shrink-0 place-items-center text-on-plate transition-colors hover:bg-plate disabled:opacity-60';

/**
 * Um arquivo anexado, numa linha só — nos relatórios da linha do tempo e no dossiê.
 *
 * Três partes emendadas: à esquerda, o **quadro cinza** (`plate-soft`) com o ícone
 * da família e a extensão; no meio, a **faixa clara** (`sunken`) com o nome e o
 * tamanho; à direita, a **ponta cinza** de novo, com ver e baixar separados
 * por filete. **A linha inteira é o botão**: abre o arquivo numa aba quando o
 * navegador mostra o formato (PDF, imagem), e baixa quando não mostra — antes
 * era preciso achar o olho, pequeno e cinza, no canto. Sob o ponteiro, a
 * faixa escurece um degrau e a barra laranja cresce do meio no pé, o gesto da
 * miniatura da galeria.
 */
export function LinhaDeArquivo({
  obraId,
  documentId,
  originalName,
  sizeBytes,
  url,
  titulo,
  detalhes,
  acoes,
  rodape,
  substituido = false,
}: {
  obraId: string;
  documentId: string;
  originalName: string;
  sizeBytes: number;
  url: string;
  /** O que a linha mostra em destaque; sem ele, o nome do arquivo. */
  titulo?: string | undefined;
  /**
   * Linhas abaixo do título, no lugar do tamanho sozinho — no dossiê, de onde
   * o arquivo veio, quem enviou e quando. O tamanho continua sendo dito: quem
   * passa `detalhes` o inclui.
   */
  detalhes?: ReactNode;
  /** Ações a mais na ponta, depois de baixar (`<AcaoDaPonta>`): editar, retirar, histórico. */
  acoes?: ReactNode;
  /**
   * A tira de informações embaixo da linha — no dossiê, quem enviou, quando, o
   * tamanho e o nome do arquivo. A linha continua na altura padrão (48px), e o
   * que não cabe nela vai para a tira, em vez de esticá-la para baixo.
   */
  rodape?: ReactNode;
  /**
   * Versão que já foi trocada por outra: o nome riscado em `danger`. Ela
   * continua aberta porque pode estar citada num relatório — mas quem olha
   * precisa ver, antes de clicar, que não é este o arquivo em vigor.
   */
  substituido?: boolean | undefined;
}) {
  const extensao = extensionOf(originalName);
  const visualizavel = isViewableInBrowser(extensao);
  const { baixando, falhou, baixar } = useBaixar(obraId, documentId, originalName);
  const Icone = ICONE_DA_FAMILIA[fileKindOf(extensao)];
  const destaque = titulo ?? originalName;

  const conteudo = (
    <>
      <span className="grid w-12 shrink-0 place-items-center bg-plate-soft text-on-plate">
        <span className="flex flex-col items-center gap-1">
          <Icone {...ICON} size={16} aria-hidden />
          <span className="font-mono text-[9px] leading-none tracking-wide uppercase">
            {extensao || '—'}
          </span>
        </span>
      </span>

      <span className="relative flex min-w-0 flex-1 flex-col justify-center gap-0.5 bg-sunken px-3.5 py-2 text-left transition-colors group-hover:bg-sunken-2">
        <span
          className={cn(
            'truncate text-[13px] font-medium',
            substituido ? 'text-danger line-through' : 'text-ink',
          )}
        >
          {destaque}
        </span>
        {falhou ? (
          <span role="alert" className="truncate text-[12px] text-danger">
            Não foi possível baixar o arquivo. Tente de novo.
          </span>
        ) : baixando ? (
          <span className="text-[12px] text-ink-3">Baixando…</span>
        ) : (
          (detalhes ?? (
            <span className="tabular text-[12px] text-ink-3">{formatBytes(sizeBytes)}</span>
          ))
        )}
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out-quint group-hover:scale-x-100 group-focus-visible:scale-x-100 motion-reduce:transition-none"
        />
      </span>
    </>
  );

  /*
   * Lado a lado enquanto o nome tem ao menos 16rem; abaixo disso (celular, ou
   * muitas ações), a ponta desce para uma barra própria, na largura toda, e o
   * nome não é espremido até sumir. Os pesos de crescimento fazem a faixa
   * clara ficar com a sobra na mesma linha, e a ponta, quando sozinha.
   */
  const principal = 'group flex min-w-0 grow-[999] basis-64 items-stretch';

  return (
    <div>
      <div className="flex min-h-12 flex-wrap items-stretch">
        {visualizavel ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            title={`Abrir ${destaque}`}
            aria-label={`Abrir ${destaque}`}
            className={principal}
          >
            {conteudo}
          </a>
        ) : (
          <button
            type="button"
            onClick={baixar}
            disabled={baixando}
            title={`Baixar ${destaque}`}
            aria-label={`Baixar ${destaque}`}
            className={cn(principal, 'disabled:cursor-progress')}
          >
            {conteudo}
          </button>
        )}

        <span className="flex min-h-12 grow justify-end divide-x divide-on-plate/20 bg-plate-soft">
          {visualizavel ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              title={`Visualizar ${originalName}`}
              aria-label={`Visualizar ${originalName}`}
              className={ACAO_DA_PONTA}
            >
              <Eye {...ICON} size={16} aria-hidden />
            </a>
          ) : null}
          <button
            type="button"
            onClick={baixar}
            disabled={baixando}
            title={`Baixar ${originalName}`}
            aria-label={`Baixar ${originalName}`}
            className={ACAO_DA_PONTA}
          >
            {baixando ? (
              <Loader2 {...ICON} size={16} className="animate-spin" aria-hidden />
            ) : falhou ? (
              <CircleAlert {...ICON} size={16} aria-hidden />
            ) : (
              <Download {...ICON} size={16} aria-hidden />
            )}
          </button>
          {acoes}
        </span>
      </div>
      {rodape ? (
        <div className="flex min-h-9 flex-wrap items-center justify-end gap-x-6 gap-y-1.5 border-x border-b border-line bg-surface-2 py-2 pr-3.5 pl-[62px] text-[12px] leading-5 text-ink-3 shadow-[inset_0_1px_0_var(--color-line-2)]">
          {rodape}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Uma ação a mais na ponta da linha de arquivo — editar, retirar, abrir o
 * histórico —, do mesmo quadrado das de ver e baixar. `perigo` acende em
 * vermelho sob o ponteiro; `ativa` fica na chapa cheia (o histórico aberto).
 */
export function AcaoDaPonta({
  rotulo,
  onClick,
  perigo = false,
  ativa,
  children,
}: {
  rotulo: string;
  onClick: () => void;
  perigo?: boolean;
  /** Para ação que abre e fecha alguma coisa: o estado vai para o leitor de tela. */
  ativa?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={rotulo}
      aria-label={rotulo}
      aria-expanded={ativa}
      className={cn(ACAO_DA_PONTA, perigo && 'hover:bg-danger', ativa && 'bg-plate')}
    >
      {children}
    </button>
  );
}

/**
 * Um dado da tira embaixo da linha: o ícone diz o que é, o texto diz o valor,
 * e o rótulo vai para o leitor de tela e para a dica. Sem "Enviado por:" escrito
 * em cada item — com três itens por anexo, o rótulo virava metade da tira.
 *
 * `className` dá a largura da coluna: com a mesma largura em todo anexo, quem,
 * quando e quanto ficam alinhados de uma linha para a outra, como numa tabela.
 */
export function InfoDoArquivo({
  icone: Icone,
  rotulo,
  children,
  className,
}: {
  icone: LucideIcon;
  rotulo: string;
  children: ReactNode;
  className?: string | undefined;
}) {
  return (
    <span title={rotulo} className={cn('flex min-w-0 shrink-0 items-center gap-2', className)}>
      <Icone {...ICON} size={14} className="shrink-0" aria-hidden />
      <span className="sr-only">{rotulo}:</span>
      <span className="tabular truncate">{children}</span>
    </span>
  );
}
